import { useEffect } from 'react';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema, SpeakableSchema, FAQSchema } from '@/components/SEOSchemas';

const PLANS = [
  { name: "Free Basic Access", price: "P0/forever", desc: "Take the first step with zero commitment", features: ["Store up to 5 critical documents", "Zero-Knowledge encrypted vault", "Basic Geo-Security", "Start building your roadmap"] },
  { name: "Standard Plan", price: "P249/mo", desc: "The essential safety net for Global Pinoys", features: ["Online SPA (Apostille-ready)", "1 Digital Executor (Trusted Backup)", "100GB encrypted storage", "Unlimited document uploads", "Business Continuity tools", "Non-watermarked PDF downloads"], popular: true },
  { name: "Family/Premium", price: "P499/mo", desc: "The ultimate legacy fortress", features: ["Up to 3 Legacy Contacts", "Unlimited encrypted storage", "Priority support", "Shared Legacy (coming soon)", "All Standard features included", "Multi-user family access"] },
];

const COMPARE_FAQS = [
  { question: "Which plan is right for me?", answer: "If you're just starting out, the Free plan lets you store your 5 most critical documents. If you're an OFW needing to manage property or banking remotely, the Standard plan's Online SPA is essential. For families needing maximum protection, the Premium plan offers unlimited storage and multiple legacy contacts." },
  { question: "Can I upgrade later?", answer: "Yes! You can start with the Free plan and upgrade to Standard or Premium at any time. Your documents and settings carry over." },
  { question: "Is there an annual discount?", answer: "Yes. Annual plans save 17%: Standard at P2,490/year and Premium at P4,990/year." },
];

export default function ComparePage() {
  useEffect(() => {
    setCanonical('/compare');
    setMeta('Compare LegaSecure Plans | Estate Planning Pricing', 'Compare LegaSecure estate planning plans. Free, Standard (P249/mo), and Premium (P499/mo). Find the right plan for your needs.');
  }, []);

  return (
    <div data-testid="compare-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Compare Plans' }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <FAQSchema faqs={COMPARE_FAQS} />

      <section className="bg-[var(--ls-bg)] py-12">
        <div className="ls-container text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] text-[var(--ls-navy)] mb-3">Compare LegaSecure Plans</h1>
          <p className="speakable-intro text-lg text-[var(--ls-text-light)] max-w-2xl mx-auto">Find the right plan to protect your family and assets. All plans include Zero-Knowledge encryption.</p>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {PLANS.map((plan, i) => (
              <div key={i} data-testid={`plan-card-${i}`} className={`ls-card relative ${plan.popular ? 'border-2 border-[var(--ls-gold)] ring-2 ring-[var(--ls-gold)]/20' : 'border border-[var(--ls-border)]'}`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[var(--ls-gold)] text-white text-xs font-bold rounded-full">MOST POPULAR</div>}
                <h3 className="text-xl font-bold text-[var(--ls-navy)] mb-1">{plan.name}</h3>
                <p className="text-sm text-[var(--ls-text-light)] mb-4">{plan.desc}</p>
                <p className="text-3xl font-bold text-[var(--ls-navy)] mb-6">{plan.price}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <i className="fa-solid fa-check text-[var(--ls-success)] mt-1"></i>
                      <span className="text-[var(--ls-text)]">{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="https://legasecure.com" className={`block text-center py-3 rounded-lg font-medium transition-all ${plan.popular ? 'ls-btn-gold w-full' : 'ls-btn-outline w-full'}`}>
                  {plan.price === "P0/forever" ? "Sign Up Free" : "Select Plan"}
                </a>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {COMPARE_FAQS.map((f, i) => (
                <div key={i} className="ls-card">
                  <h3 className="font-semibold text-[var(--ls-navy)] mb-2">{f.question}</h3>
                  <p className="text-sm text-[var(--ls-text-light)]">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
